import React from 'react';
import { Card, Typography, Flex, Button, Tag, Dropdown, Modal } from 'antd';
import { 
  EllipsisOutlined,
  NodeIndexOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  ExportOutlined
} from '@ant-design/icons';
import { theme } from 'antd';
import { ProcessFlow } from '../../../../types';
import { INTEGRATION_ICONS } from '../../../../constants';
import './AssistantProcessFlow.css';

const { Title, Text, Paragraph } = Typography;
const { useToken } = theme;

export interface ProcessFlowCardProps {
  workflow: ProcessFlow;
  onViewWorkflow: (workflow: ProcessFlow) => void;
  onActivate: (workflowId: string) => void;
  onDeactivate: (workflowId: string) => void;
  onDelete: (workflowId: string) => void;
}

export const ProcessFlowCard: React.FC<ProcessFlowCardProps> = ({ 
  workflow,
  onViewWorkflow,
  onActivate,
  onDeactivate,
  onDelete,
}) => {
  const { token } = useToken();

  const handleDelete = () => {
    Modal.confirm({
      title: `Delete "${workflow.name}"?`,
      content: 'This action is permanent and cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      onOk: () => onDelete(workflow.id),
    });
  };

  const dropdownItems = [
    {
      key: 'enable',
      icon: <CheckCircleOutlined />,
      label: workflow.status === 'inactive' ? 'Enable' : 'Disable',
      onClick: () => workflow.status === 'inactive' ? onActivate(workflow.id) : onDeactivate(workflow.id),
    },
    {
      key: 'delete',
      icon: <DeleteOutlined />,
      label: 'Delete',
      onClick: handleDelete,
    },
  ];

  const formatDate = (dateString: string): string => {
    const updateDate = new Date(dateString);
    const now = new Date();
    const diffTime = now.getTime() - updateDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    return `${diffDays} days ago`;
  };

  const getIconForIntegration = (integration: string): string => {
    const normalizedIntegration = integration.toLowerCase();
    return INTEGRATION_ICONS[normalizedIntegration as keyof typeof INTEGRATION_ICONS] || INTEGRATION_ICONS.gmail;
  };

  const handleViewWorkflow = () => {
    onViewWorkflow(workflow);
  };

  return (
    <Card className="process-flow-card">
      <Flex vertical className="process-flow-card-content">
        <Flex justify="space-between" align="center">
          <Title level={5} className="process-flow-card-title">{workflow.name}</Title>
          <Dropdown
            menu={{ items: dropdownItems }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button 
              icon={<EllipsisOutlined />} 
              type="text"
              onClick={(e) => e.stopPropagation()}
              id={`flow-card-menu-${workflow.id}`}
            />
          </Dropdown>
        </Flex>
        <Paragraph type="secondary" className="process-flow-card-description">
          {workflow.description}
        </Paragraph>
        <Tag className={`status-tag ${workflow.status.toLowerCase()}`}>
          {workflow.status}
        </Tag>
        <Flex gap="small" align="center" className="process-flow-card-connections">
          <Text type="secondary">Connects to:</Text>
          {workflow.integratesWith.map((conn, index) => (
            <img 
              key={index}
              src={getIconForIntegration(conn)} 
              alt={conn} 
              className="process-flow-card-connection-icon"
              style={{ width: 'auto', height: '24px', objectFit: 'contain', marginRight: '8px' }}
            />
          ))}
        </Flex>
        <Text type="secondary">Last updated: {formatDate(workflow.updatedAt)}</Text>
        <Flex gap="small" className="process-flow-card-actions">
          <Button 
            style={{ flex: 1, maxWidth: '200px' }}
            onClick={handleViewWorkflow}
            id={`view-workflow-${workflow.id}`}
          >
            View workflow
          </Button>
          <Button 
            className="process-flow-card-edit-button" 
            type="link"
            href={workflow.n8nWebhookURL}
            target="_blank"
            id={`edit-workflow-${workflow.id}`}
          >
            Edit <ExportOutlined />
          </Button>
        </Flex>
      </Flex>
    </Card>
  );
}; 